import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { concatMap, map, toArray } from 'rxjs/operators';

import {
  AUTO_DISTRIBUTION_OPTION_GROUP,
  AUTO_DISTRIBUTION_OPTION_NAMES,
  AutoDistributionOptions,
  autoDistributionOptionValue,
  isSectorOptionValueEqual,
  SectorOption,
  SectorOptionPayload,
  SectorOptionValue,
} from './sector-options';

@Injectable({
  providedIn: 'root',
})
export class SectorOptionsService {
  private readonly api = '/admin/sector_options/';

  constructor(private http: HttpClient) {}

  getOptions(sectorId: number, group = AUTO_DISTRIBUTION_OPTION_GROUP): Observable<SectorOption[]> {
    // Бэкенд пока не фильтрует по sector/group — запрашиваем всё и отбираем записи участка на фронте
    return this.http
      .get<any>(this.api)
      .pipe(
        map((response) =>
          this.extractList(response).filter(
            (option) => option && Number(option.sector) === Number(sectorId) && Number(option.group) === Number(group)
          )
        )
      );
  }

  createOption(payload: SectorOptionPayload): Observable<SectorOption> {
    return this.http.post<any>(this.api, payload).pipe(map((response) => this.extractItem(response, payload)));
  }

  /** Обновляет значение записи; поле (number_value или char_value) зависит от типа настройки */
  updateOption(option: SectorOption, optionValue: SectorOptionValue): Observable<SectorOption> {
    const body: Partial<SectorOption> =
      optionValue.field === 'char_value' ? { char_value: String(optionValue.value) } : { number_value: Number(optionValue.value) };

    return this.http
      .patch<any>(`${this.api}${option.id}/`, body)
      .pipe(map((response) => this.extractItem(response, Object.assign({}, option, body))));
  }

  deleteOption(id: number): Observable<unknown> {
    return this.http.delete(`${this.api}${id}/`);
  }

  getAutoDistributionOptions(sectorId: number): Observable<SectorOption[]> {
    return this.getOptions(sectorId, AUTO_DISTRIBUTION_OPTION_GROUP);
  }

  /**
   * Сохраняет набор настроек автораспределения: существующие записи обновляет (PATCH),
   * недостающие создаёт (POST). Запросы идут последовательно, чтобы при частичной ошибке
   * повторное сохранение не создавало дублей.
   */
  saveAutoDistributionOptions(sectorId: number, records: SectorOption[], options: AutoDistributionOptions): Observable<SectorOption[]> {
    return from(AUTO_DISTRIBUTION_OPTION_NAMES).pipe(
      concatMap((name) => {
        const optionValue = autoDistributionOptionValue(options, name);
        const record = (records || []).find((item) => item.name === name);

        if (record) {
          return isSectorOptionValueEqual(record, optionValue) ? of(record) : this.updateOption(record, optionValue);
        }

        const payload: SectorOptionPayload = {
          sector: Number(sectorId),
          group: AUTO_DISTRIBUTION_OPTION_GROUP,
          name,
        };

        if (optionValue.field === 'char_value') {
          payload.char_value = String(optionValue.value);
        } else {
          payload.number_value = Number(optionValue.value);
        }

        return this.createOption(payload);
      }),
      toArray()
    );
  }

  deleteAutoDistributionOptions(records: SectorOption[]): Observable<unknown> {
    const ids = (records || []).map((record) => record.id).filter((id) => id !== null && id !== undefined);

    if (!ids.length) {
      return of(null);
    }

    return from(ids).pipe(
      concatMap((id) => this.deleteOption(id)),
      toArray()
    );
  }

  /** Ответ может прийти массивом, в обёртке { data } или с пагинацией { results } */
  private extractList(response: any): SectorOption[] {
    const data = response && response.data !== undefined ? response.data : response;

    if (Array.isArray(data)) {
      return data;
    }

    if (data && Array.isArray(data.results)) {
      return data.results;
    }

    return [];
  }

  private extractItem(response: any, fallback: Partial<SectorOption>): SectorOption {
    const data = response && response.data !== undefined ? response.data : response;

    return Object.assign({}, fallback, data && typeof data === 'object' ? data : {}) as SectorOption;
  }
}
